/**
 * Error bridge — module-level shim that lets Sentry's `beforeSend`
 * (which runs BEFORE React mounts in `main.tsx`) route JS-error
 * events into the activity log.
 *
 * The provider registers a listener on mount; pre-mount errors are
 * buffered (cap 32) and replayed once the listener is installed.
 *
 *   sentry.ts beforeSend → reportErrorToActivity(...)
 *                            ↓
 *                         (buffer until listener set)
 *                            ↓
 *                  <UserActivityProvider> mount
 *                            ↓
 *                  drainErrorBuffer() → listener fires
 */

export interface ErrorBridgeEvent {
  fingerprint?: string;
  message: string;
}

const MAX_BUFFER = 32;
const buffer: ErrorBridgeEvent[] = [];
let listener: ((e: ErrorBridgeEvent) => void) | null = null;

/** Sentry beforeSend / global window.onerror routes here. */
export function reportErrorToActivity(event: ErrorBridgeEvent): void {
  if (listener) {
    listener(event);
    return;
  }
  if (buffer.length >= MAX_BUFFER) buffer.shift();
  buffer.push(event);
}

/** Provider mount registers + drains. */
export function setErrorListener(
  fn: ((e: ErrorBridgeEvent) => void) | null,
): void {
  listener = fn;
}

/** Drain the pre-mount buffer through the active listener. */
export function drainErrorBuffer(): void {
  if (!listener) return;
  for (const e of buffer) listener(e);
  buffer.length = 0;
}
